// booking-mock.ts — All static booking-flow mock data.
// TODO(booking-API-PR): replace every export here with real BE calls.

// ─── FARE TIERS ──────────────────────────────────────────────────────────────
export interface FareTier {
  k: 'light' | 'standard' | 'flex' | 'business';
  label: string;
  /** Price in paise (INR). Display as ₹ with no decimals. */
  pricePaise: number;
  /** Display string already formatted for mock. */
  priceDisplay: string;
  bag: string;
  cabin: string;
  change: string;
  cancel: string;
  seat: string;
  meal: string;
  tag: 'POPULAR' | 'BEST VALUE' | null;
}

export const FARE_TIERS: FareTier[] = [
  {
    k: 'light',
    label: 'Light',
    pricePaise: 3508300,
    priceDisplay: '₹35,083',
    bag: 'No checked bag',
    cabin: '7 kg carry-on',
    change: 'No changes',
    cancel: 'No refund',
    seat: 'Not included',
    meal: 'Paid meals',
    tag: null,
  },
  {
    k: 'standard',
    label: 'Standard',
    pricePaise: 4066100,
    priceDisplay: '₹40,661',
    bag: '20 kg checked bag',
    cabin: '7 kg carry-on',
    change: 'Paid changes',
    cancel: 'Partial refund',
    seat: 'Not included',
    meal: 'Paid meals',
    tag: 'POPULAR',
  },
  {
    k: 'flex',
    label: 'Flex',
    pricePaise: 4649900,
    priceDisplay: '₹46,499',
    bag: '25 kg checked bag',
    cabin: '10 kg carry-on',
    change: '1 free date change',
    cancel: 'Full refund',
    seat: 'Included',
    meal: '1 meal included',
    tag: 'BEST VALUE',
  },
  {
    k: 'business',
    label: 'Business',
    pricePaise: 7420800,
    priceDisplay: '₹74,208',
    bag: '2×32 kg bags',
    cabin: '15 kg carry-on',
    change: 'Unlimited changes',
    cancel: 'Full refund',
    seat: 'Lie-flat',
    meal: 'All meals',
    tag: null,
  },
];

// ─── PASSENGER DEFAULT ────────────────────────────────────────────────────────
export interface Passenger {
  title: string;
  firstName: string;
  lastName: string;
  dob: string;
  nationality: string;
  passport: string;
  expiry: string;
  type: 'adult' | 'child';
}

export const PASSENGER_DEFAULT: Passenger = {
  title: 'Mr',
  firstName: 'Alex',
  lastName: 'Chen',
  dob: '1992-03-15',
  nationality: 'India',
  passport: 'P1234567',
  expiry: '2029-06-30',
  type: 'adult',
};

export const PASSENGER_TITLES = ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'] as const;

export const NATIONALITIES = [
  'India', 'United States', 'United Kingdom', 'Australia',
  'Singapore', 'Japan', 'Germany', 'France', 'Canada', 'Other',
] as const;

// ─── SEAT GRID ────────────────────────────────────────────────────────────────
export const SEAT_ROWS = 28;
export const SEAT_COLS = ['A', 'B', 'C', 'D', 'E', 'F'] as const;

export type SeatCol = typeof SEAT_COLS[number];

export type SeatTier = 'premium' | 'exit' | 'standard';

export function getSeatTier(row: number): SeatTier {
  if (row <= 4) return 'premium';
  if (row === 13 || row === 14) return 'exit';
  return 'standard';
}

/** Upgrade fee in paise. 0 for standard. */
export function getSeatUpgradePaise(row: number): number {
  if (row <= 4) return 208000; // ₹2,500 in paise (approx)
  if (row === 13 || row === 14) return 124800; // ₹1,500 in paise
  return 0;
}

export const OCCUPIED_SEATS = new Set([
  '1B','2A','2C','2F','3D','3E','4B','4F',
  '7A','7C','8B','8E','9D','9F','10A','10C',
  '11B','12D','12F','13B','13E','14A','14D',
  '15C','15F','16B','17A','17D','18C','18F',
  '19B','19E','20A','20D','21C','22F','22B',
  '23A','23E','24D','25C','25F','26A','26B',
  '27D','27E','28B','28E','28F',
]);

// ─── PAYMENT METHODS ─────────────────────────────────────────────────────────
export type PaymentMethod = 'card' | 'upi' | 'netbanking' | 'wallet';

export const PAYMENT_METHODS: Array<{ k: PaymentMethod; label: string; icon: string }> = [
  { k: 'card',       label: 'Credit / Debit card', icon: '💳' },
  { k: 'upi',        label: 'UPI',                  icon: '⚡' },
  { k: 'netbanking', label: 'Net banking',           icon: '🏦' },
  { k: 'wallet',     label: 'Wallet',                icon: '👛' },
];

export const NET_BANKING_OPTIONS = [
  'HDFC Bank', 'ICICI Bank', 'State Bank of India',
  'Axis Bank', 'Kotak Mahindra Bank', 'Other',
] as const;

// ─── FLIGHT SUMMARY (mock) ────────────────────────────────────────────────────
export const MOCK_FLIGHT = {
  airline:   'Singapore Airlines',
  code:      'SQ',
  logoColor: '#1B3A6B',
  flightNo:  'SQ 433',
  aircraft:  'Airbus A350',
  from:      'BLR',
  fromCity:  'Bengaluru',
  to:        'DPS',
  toCity:    'Bali',
  depart:    '23:55',
  arrive:    '12:10',
  duration:  '11h 15m',
  stops:     '1 stop · SIN',
  date:      'Tue, 17 Mar 2026',
  arriveDate:'Wed, 18 Mar 2026',
  co2Label:  'Avg emissions',
  score:     96,
} as const;

// ─── PRICE BREAKDOWN ─────────────────────────────────────────────────────────
/** Airport taxes in paise */
export const AIRPORT_TAXES_PAISE = 3993600; // ~₹3,994 placeholder

/** Platform fee — 0 for now. */
export const PLATFORM_FEE_PAISE = 0;

/** Travel insurance add-on in paise */
export const INSURANCE_PAISE = 33200; // ₹399

export function formatPaise(paise: number): string {
  return `₹${Math.round(paise / 100).toLocaleString('en-IN')}`;
}

// ─── PNR GENERATOR ────────────────────────────────────────────────────────────
// TODO(booking-API-PR): replace with real PNR from BE /bookings response.
export function generatePNR(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let suffix = '';
  for (let i = 0; i < 4; i++) {
    suffix += chars[Math.floor(Math.random() * chars.length)];
  }
  return `GF-2026-${suffix}`;
}

// ─── FREQUENT FLYER PROGRAMS ──────────────────────────────────────────────────
export const FF_PROGRAMS = [
  { v: 'SQ', l: 'Singapore KrisFlyer' },
  { v: 'SY', l: 'SkyTeam' },
  { v: 'QR', l: 'Qatar Privilege Club' },
  { v: 'EK', l: 'Emirates Skywards' },
  { v: 'other', l: 'Other' },
] as const;
